import React from 'react';
import { Button } from '@fluentui/react-components';
import { CheckmarkCircle24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ContractReview, ContractReviewFinding, FindingDecision } from '../../../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../../../models/HttpError';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { WorkStateFilter } from '../ContractReview.Functions';
import { ContractReviewItemCard } from '../ContractReviewItemCard/ContractReviewItemCard.Component';

/*
 * La lista de trabajo: una tarjeta por cambio o aviso, en el orden de importancia. Cuando no queda nada en el estado que
 * se mira, lo dice con palabras y ofrece el paso siguiente (ver todos, aplicar).
 */

export interface ContractReviewWorkListProps {
  review: ContractReview;
  findings: ContractReviewFinding[];
  /** Estado que se está viendo; decide el texto del vacío. */
  state: WorkStateFilter;
  /** Hay búsqueda, tipo o concepto filtrando (el vacío ofrece quitarlos). */
  filtered: boolean;
  /** No hay nada en la revisión. */
  nothingAtAll: boolean;
  onClearFilters: () => void;
  onViewAll: () => void;
  /** Acción principal para el vacío de "por decidir" cuando hay algo para aplicar. */
  applyAction?: { label: string; onClick: () => void };
  selectedIds?: number[];
  onSelect?: (id: number, checked: boolean) => void;
  decidingIds?: number[];
  decisionErrors?: Record<number, ErrorType | undefined>;
  onDecide?: (finding: ContractReviewFinding, decision: FindingDecision) => void;
  onUndo?: (finding: ContractReviewFinding) => void;
  undoingIds?: number[];
  undoErrors?: Record<number, ErrorType | undefined>;
}

export const ContractReviewWorkList: React.FC<ContractReviewWorkListProps> = props => {
  const { t } = useTranslation();

  if (props.findings.length === 0) {
    if (props.nothingAtAll) return <GuideEmptyState title={t('contractReview.empty.noFindings')} data-testid="findings-empty" />;
    if (props.filtered) {
      return (
        <GuideEmptyState
          title={t('contractReview.work.empty.filteredTitle')}
          text={t('contractReview.work.empty.filteredText')}
          action={<Button onClick={props.onClearFilters}>{t('contractReview.work.empty.clear')}</Button>}
          data-testid="findings-empty"
        />
      );
    }
    const done = props.state === 'pending';
    return (
      <GuideEmptyState
        icon={done ? <CheckmarkCircle24Regular /> : undefined}
        title={t(`contractReview.work.empty.${props.state}Title`)}
        text={done ? t('contractReview.work.empty.pendingText') : undefined}
        action={
          <span className="crv-empty__actions">
            {done && props.applyAction && (
              <Button appearance="primary" onClick={props.applyAction.onClick}>
                {props.applyAction.label}
              </Button>
            )}
            {props.state !== 'all' && <Button onClick={props.onViewAll}>{t('contractReview.work.empty.viewAll')}</Button>}
          </span>
        }
        data-testid="findings-empty"
      />
    );
  }

  return (
    <div className="crv-list" role="list" aria-label={t('contractReview.work.title')} data-testid="findings-list">
      {props.findings.map(finding => (
        <div key={finding.id} role="listitem">
          <ContractReviewItemCard
            review={props.review}
            finding={finding}
            selectable={!!props.onSelect}
            selected={props.selectedIds?.includes(finding.id)}
            onSelect={props.onSelect ? checked => props.onSelect!(finding.id, checked) : undefined}
            deciding={props.decidingIds?.includes(finding.id)}
            decisionError={props.decisionErrors?.[finding.id]}
            onDecide={props.onDecide ? decision => props.onDecide!(finding, decision) : undefined}
            onUndo={props.onUndo ? () => props.onUndo!(finding) : undefined}
            undoing={props.undoingIds?.includes(finding.id)}
            undoError={props.undoErrors?.[finding.id]}
          />
        </div>
      ))}
    </div>
  );
};

export default ContractReviewWorkList;
